import { useContext } from "react";

import { ClickPopper } from "@client/components/utils/ClickPopper";
import KebabMenuIcon from "@client/icons/KebabMenuIcon";
import { DispatchContext, StateContext } from "@client/pages/round/Round";
import { getEvaluator, isUserRoundAdmin } from "@engine/round/accessors";
import { Evaluation, Funder, Recommender } from "@engine/round/aggregate";
import { isZeroMVF, type MVF } from "@lib/tranche";
import { Evaluable } from "@lib/types";
import { FrontendEvaluation } from "../useEvaluatorEvaluationsCache";

export const ActionButton = ({
  onClick,
  text,
}: {
  onClick: () => void;
  text: string;
}) => {
  return (
    <button
      className="dark:text-white dark:bg-stone-800 rounded-none hover:bg-stone-200 dark:hover:bg-stone-400"
      onClick={onClick}
    >
      {text}
    </button>
  );
};

export function EvaluableCardControlsMenu({
  evaluable,
  evaluator,
  evaluation,
  onPopperShow,
  onPopperHide,
  onEditParameters,
}: {
  evaluable: Evaluable;
  evaluator?: Recommender | Funder;
  evaluation?: FrontendEvaluation | Evaluation;
  onPopperShow?: () => void;
  onPopperHide?: () => void;
  onEditParameters?: () => void;
}) {
  const { round, actingAsUserId: userId } = useContext(StateContext);
  const dispatch = useContext(DispatchContext);

  const evaluationState = evaluation?.state ?? "not-started";

  const meanRecommenders: Recommender[] = [];
  for (const member of Object.values(round.members)) {
    for (const evaluator of Object.values(member.evaluators)) {
      if (
        evaluator.type === "recommender" &&
        evaluator.meanRecommenderThreshold != null
      ) {
        meanRecommenders.push(evaluator);
      }
    }
  }

  const userHasPermissionToModifyEvaluation =
    isUserRoundAdmin(round, userId) || evaluator?.memberId === userId;

  function setToZero() {
    if (!evaluator) {
      return;
    }

    void dispatch({
      type: "EVALUATION_SET_ZERO",
      evaluableId: evaluable.id,
      evaluatorId: evaluator.id,
    });
  }

  function evaluationStart() {
    if (!evaluator) {
      return;
    }

    let mvf: MVF;
    if (evaluable.type === "proposal") {
      const baseRequestAmount = evaluable.typedFields.baseRequestAmount;
      mvf = [
        { dollar: 0, value: 0.5, placedManually: true },
        { dollar: baseRequestAmount, value: 0.5, placedManually: true },
        { dollar: baseRequestAmount, value: 0, placedManually: true },
      ];
    } else {
      mvf = [
        { dollar: 0, value: 1, placedManually: true },
        { dollar: 2_000_000, value: 0, placedManually: true },
      ];
    }

    void dispatch({
      type: "START_EVALUATION",
      evaluatorId: evaluator.id,
      evaluableId: evaluable.id,
      mvf,
    });
  }

  function evaluationCancel() {
    if (!evaluator) {
      return;
    }

    void dispatch({
      type: "EVALUATION_STATUS_CHANGE",
      evaluatorId: evaluator.id,
      evaluableId: evaluable.id,
      change: "cancel",
    });
  }

  function hide() {
    if (!evaluator) {
      return;
    }

    void dispatch({
      type: "EVALUATION_STATUS_CHANGE",
      evaluatorId: evaluator.id,
      evaluableId: evaluable.id,
      change: "hide",
    });
  }

  function unhide() {
    if (!evaluator) {
      return;
    }

    void dispatch({
      type: "EVALUATION_STATUS_CHANGE",
      evaluatorId: evaluator.id,
      evaluableId: evaluable.id,
      change: "unhide",
    });
  }

  function deferTo(recommenderId: string) {
    if (!evaluator) {
      return;
    }

    void dispatch({
      type: "EVALUATION_STATUS_CHANGE",
      evaluatorId: evaluator.id,
      evaluableId: evaluable.id,
      change: "defer",
      deferredTo: recommenderId,
    });
  }

  function removeDeferral() {
    if (!evaluator) {
      return;
    }

    void dispatch({
      type: "EVALUATION_STATUS_CHANGE",
      evaluatorId: evaluator.id,
      evaluableId: evaluable.id,
      change: "recuse",
    });
  }

  const isDeferred =
    evaluator &&
    evaluator.type === "recommender" &&
    evaluationState === "deferred";

  let deferredToName = "";
  if (isDeferred) {
    const deferredTo = evaluation!.deferredTo;
    if (!deferredTo) {
      throw new Error("deferredTo is not set");
    }
    deferredToName = getEvaluator(round, deferredTo)?.shortname ?? "";
  }

  // ECA: You can take this syntactic sugar from my cold dead hands :cold_face:
  const stateIs = (state: string) => evaluationState === state;
  const stateIsIn = (states: string[]) => states.includes(evaluationState);

  const evaluationHasNonZeroMVF =
    evaluation &&
    "mvf" in evaluation &&
    evaluation.mvf &&
    !isZeroMVF(evaluation.mvf);

  return (
    <div>
      {evaluator && userHasPermissionToModifyEvaluation && (
        <ClickPopper
          onShow={onPopperShow}
          onHide={onPopperHide}
          placement={onEditParameters ? "left-start" : "right-start"}
          title={
            <div className="flex flex-col w-48">
              {stateIs("evaluated") && onEditParameters && (
                <ActionButton
                  onClick={onEditParameters}
                  text="Edit Parameters"
                />
              )}
              {stateIsIn(["not-started", "hidden", "canceled"]) && (
                <ActionButton
                  onClick={evaluationStart}
                  text="Start Evaluation"
                />
              )}
              {stateIs("evaluated") && (
                <ActionButton
                  onClick={evaluationCancel}
                  text="Cancel Evaluation"
                />
              )}
              {(stateIsIn(["not-started", "hidden", "canceled"]) ||
                (stateIs("evaluated") && evaluationHasNonZeroMVF)) && (
                <ActionButton
                  onClick={setToZero}
                  text={stateIs("evaluated") ? "Set to 0" : "Give 0 Evaluation"}
                />
              )}
              {stateIsIn(["not-started", "canceled"]) && (
                <ActionButton onClick={hide} text="Hide" />
              )}
              {stateIs("hidden") && (
                <ActionButton onClick={unhide} text="Unhide" />
              )}
              {evaluator.type === "recommender" &&
                (stateIs("deferred") ? (
                  <ActionButton
                    onClick={() => removeDeferral()}
                    text={`Remove deferral to ${deferredToName}`}
                  />
                ) : stateIs("recused") ? (
                  meanRecommenders.map((recommender, i) => (
                    <ActionButton
                      key={i}
                      onClick={() => deferTo(recommender.id)}
                      text={`Defer to ${recommender.shortname}`}
                    />
                  ))
                ) : null)}
            </div>
          }
        >
          <KebabMenuIcon />
        </ClickPopper>
      )}
    </div>
  );
}
